import { useState, useRef, useEffect, FC } from 'react';
import styled from 'styled-components';
import { Button, Card, Flex } from 'rebass';

const StickyButton = styled(Button) <{ isOpen: boolean }>`
  position: sticky;
  top: 12px;
  right: 12px;
  float: right;
  margin-left: auto !important;
  background-color: #5f7497;
  width: 46.0703px;
  height: fit-content ;
  z-index: 10;
  opacity:${({ isOpen }) => isOpen ? 1 : 0.45} ;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  &:hover{
    opacity:1 ;
  }
`;

const MenuEntryWrap = styled(Flex) <{ isDisabled: boolean, isLast?: boolean }>`
  width: 100%;
  background-color:${({ isDisabled }) => isDisabled ? '#e3e3e3' : '#5f7497'} ;
  transition: all 0.35s ease-in-out;
  padding: 12px 16px;
  font-size: 14px;
  border-bottom:${({ isLast }) => isLast ? '1px solid transparent' : '1px solid rgba(255,255,255,0.3)'}  ;
  color: ${({ isDisabled }) => isDisabled ? '#afafaf' : 'white'} ;
  cursor:  ${({ isDisabled }) => isDisabled ? 'not-allowed' : 'pointer'} ;
  user-select: none;

  &:hover{
    background-color:${({ isDisabled }) => isDisabled ? '#e3e3e3' : '#455877'} ;
  }
`

export interface IMenuEntry {
  text: string
  onClick: () => void
  isDisabled?: boolean
}

const MenuEntry: FC<IMenuEntry & { isLast?: boolean }> = ({
  text, onClick, isDisabled, isLast
}) => {
  return (
    <MenuEntryWrap isLast={isLast} onClick={onClick} isDisabled={!!isDisabled}>
      {text}
    </MenuEntryWrap>
  )
}

const AnimatedMenu = styled(Card) <{ isOpen: boolean }>`
  position: sticky;
  top: 49px;
  right: 12px;
  margin-left:auto !important;
  border-radius:8px ;
  margin-bottom: -199px !important ;
  transform: ${({ isOpen }) =>
    isOpen ? 'translateX(0) translateY(0)' : 'translateX(0) translateY(-10px)'};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transition: all 0.2s ease-in-out;
  z-index: 9;
  background-color: #5f7497;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  color: white;
  display: ${({ isOpen }) => isOpen ? 'initial' : 'none'};
`;

interface IMenu {
  entries: IMenuEntry[]
}

const ScrollMenu: FC<IMenu> = ({ entries }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };

    if (menuOpen && menuRef && menuRef.current) {
      const height = menuRef.current.offsetHeight;
      menuRef.current.style.setProperty('--expanded-height', `${height}px`);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);
  

  return (
    <>
      <StickyButton isOpen={menuOpen} ref={buttonRef} onClick={() => setMenuOpen((prev) => !prev)}>
        {menuOpen ? 'x' : '☰'}
      </StickyButton>

      <AnimatedMenu ref={menuRef} p={2} isOpen={menuOpen}>
        {entries.map((entry, index) => (
          <MenuEntry
            key={index}
            text={entry.text}
            onClick={() => {
              entry.onClick();
              setMenuOpen(false);
            }}
            isDisabled={entry.isDisabled}
            isLast={index === entries.length - 1}
          />
        ))}
      </AnimatedMenu>
    </>
  );
};

export default ScrollMenu;
